'use client';

import React, { useRef, useEffect, useState } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { cn } from '../lib/utils';
import { useMousePosition, useViewport, useInView } from '../hooks';

export interface HeroMediaProps {
  desktopVideo?: string;
  desktopImage: string;
  mobileVideo?: string;
  mobileImage: string;
  alt: string;
  overlay?: boolean;
  parallax?: boolean;
  className?: string;
  children?: React.ReactNode;
}

/**
 * Desktop: Cinematic video/parallax image (16:9)
 * Mobile: Full-height vertical video/image (9:16)
 */
export const HeroMedia: React.FC<HeroMediaProps> = ({
  desktopVideo,
  desktopImage,
  mobileVideo,
  mobileImage,
  alt,
  overlay = true,
  parallax = true,
  className,
  children,
}) => {
  const { isDesktop, isMobile } = useViewport();
  const { normalizedPosition } = useMousePosition();
  const { ref, isInView } = useInView<HTMLDivElement>({ threshold: 0.3 });
  const videoRef = useRef<HTMLVideoElement>(null);
  
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  // Parallax effect - subtle pan on mouse movement
  const mouseX = useSpring(useMotionValue(0), { damping: 40, stiffness: 200 });
  const mouseY = useSpring(useMotionValue(0), { damping: 40, stiffness: 200 });

  useEffect(() => {
    if (parallax && isDesktop) {
      mouseX.set(normalizedPosition.x * 3);
      mouseY.set(normalizedPosition.y * 3);
    }
  }, [normalizedPosition, parallax, isDesktop, mouseX, mouseY]);

  // Video playback control
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isInView && isPlaying) {
      video.play().catch(() => setIsPlaying(false));
    } else {
      video.pause();
    }
  }, [isInView, isPlaying]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    
    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    
    video.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const videoSrc = isDesktop ? desktopVideo : mobileVideo;
  const imageSrc = isDesktop ? desktopImage : mobileImage;

  return (
    <div
      ref={ref}
      className={cn(
        'relative overflow-hidden',
        isMobile ? 'aspect-hero-mobile' : 'aspect-hero-desktop',
        className
      )}
    >
      {/* Media Container */}
      <motion.div
        className="absolute inset-0"
        style={{
          x: parallax && isDesktop ? mouseX : 0,
          y: parallax && isDesktop ? mouseY : 0,
          scale: parallax ? 1.1 : 1,
        }}
      >
        {/* Video (if available) */}
        {videoSrc && (
          <video
            ref={videoRef}
            className={cn(
              'absolute inset-0 w-full h-full object-cover',
              !isVideoLoaded && 'opacity-0'
            )}
            src={videoSrc}
            muted={isMuted}
            loop
            playsInline
            onLoadedData={() => setIsVideoLoaded(true)}
          />
        )}

        {/* Fallback Image */}
        <img
          src={imageSrc}
          alt={alt}
          className={cn(
            'absolute inset-0 w-full h-full object-cover',
            videoSrc && isVideoLoaded && 'opacity-0'
          )}
          loading="eager"
        />
      </motion.div>

      {/* Gradient Overlay */}
      {overlay && (
        <div className="absolute inset-0 bg-gradient-to-t from-surface-0 via-surface-0/50 to-transparent" />
      )}

      {/* Video Controls (Desktop only) */}
      {videoSrc && isDesktop && (
        <div className="absolute bottom-6 right-6 flex gap-2">
          <button
            onClick={togglePlay}
            className="p-3 rounded-full bg-surface-0/60 backdrop-blur-sm border border-white/10 hover:bg-surface-0/80 transition-colors"
            aria-label={isPlaying ? 'Pause video' : 'Play video'}
          >
            {isPlaying ? (
              <Pause className="w-4 h-4 text-white" />
            ) : (
              <Play className="w-4 h-4 text-white" />
            )}
          </button>
          <button
            onClick={toggleMute}
            className="p-3 rounded-full bg-surface-0/60 backdrop-blur-sm border border-white/10 hover:bg-surface-0/80 transition-colors"
            aria-label={isMuted ? 'Unmute video' : 'Mute video'}
          >
            {isMuted ? (
              <VolumeX className="w-4 h-4 text-white" />
            ) : (
              <Volume2 className="w-4 h-4 text-white" />
            )}
          </button>
        </div>
      )}

      {/* Content Overlay */}
      {children && (
        <div className="absolute inset-0 flex items-end">
          <div className="w-full p-6 md:p-12">
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Property Image Gallery with zoom capability
 */
export interface PropertyGalleryProps {
  images: { src: string; alt: string }[];
  className?: string;
}

export const PropertyGallery: React.FC<PropertyGalleryProps> = ({
  images,
  className,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const { isDesktop } = useViewport();

  if (images.length === 0) return null;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Main Image */}
      <motion.div
        className={cn(
          'relative overflow-hidden rounded-vault-lg',
          isDesktop ? 'aspect-video' : 'aspect-square'
        )}
        layoutId="gallery-main"
      >
        <motion.img
          key={activeIndex}
          src={images[activeIndex].src}
          alt={images[activeIndex].alt}
          className="w-full h-full object-cover"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        />
      </motion.div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={cn(
                'flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all',
                index === activeIndex
                  ? 'border-gold-500 opacity-100'
                  : 'border-transparent opacity-60 hover:opacity-100'
              )}
            >
              <img
                src={image.src}
                alt=""
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
